import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import usePointPlacement from '../../hooks/usePointPlacement';
import { SUGGEST_ALL } from '../../utils/variableSuggestions';
import { TRIGGER_ALWAYS } from './syntaxHighlight/syntaxHighlightHelpers';
import { renderSyntaxHighlightInput } from './syntaxHighlight/renderSyntaxHighlightInput';
import { useHighlightHandlers } from './syntaxHighlight/useHighlightHandlers';
import { useHighlightSync } from './syntaxHighlight/useHighlightSync';
import { useHighlightEditing } from './syntaxHighlight/useHighlightEditing';
import { useDynamicVarPicker } from './syntaxHighlight/useDynamicVarPicker';
import { useHighlightDom } from './syntaxHighlight/useHighlightDom';
export {
  TRIGGER_ALWAYS, TRIGGER_BRACES, caretInsideCompleteReference, resolveDynSuggestionState,
  resolveExpandCaret, selectionIntersectsVariable,
} from './syntaxHighlight/syntaxHighlightHelpers';

const SyntaxHighlightInput = ({ 
  value, 
  onChange, 
  placeholder, 
  className, 
  selectedEnvironment, 
  workspaceVariables,
  globalVariables,
  collectionVariables,
  // Folder chain variables, nearest first (utils/folderVariables). Resolved
  // above collection; a chip that resolves here opens the Folder variables
  // modal instead of the inline popup (the popup has no folder scope).
  folderVariables,
  onVariableUpdate, // New prop for handling variable updates
  variableContext,
  onPastePlainText, // (clipboardText) => true if parent handled paste (no insert)
  /** Called after the internal blur handler — use to sync derived state (e.g. URL → Params). */
  onBlur: onBlurProp,
  /** Bump when parent imports cURL so URL sync runs even if lag detection would skip. */
  forceSyncNonce = 0,
  disabled = false,
  /** KV fields (headers, schema values): show dynamic-variable picker on focus/click. */
  showDynamicVarPicker: showDynamicVarPickerProp,
  type = "text",
  style = {},
  inputRef = null,
  /** URL field only: expand into a floating multi-line overlay when the text overflows one line. */
  expandOnOverflow = false,
  /**
   * URL field only: right-clicking a plain-text selection offers "To Variable", which opens the
   * variable editor with the selection as the value and replaces the selection with `{{name}}`.
   * Off by default — KV fields keep the native context menu.
   */
  selectionToVariable = false,
  /**
   * Which halves of the suggestions this field offers — see
   * utils/variableSuggestions. Params and auth want both; the URL wants the
   * user's own variables only; a variable's own value wants only the generated
   * ones, since offering the table you are editing invites a variable defined
   * in terms of itself.
   */
  suggest = SUGGEST_ALL,
  /**
   * What wakes the list: TRIGGER_ALWAYS filters on anything typed;
   * TRIGGER_BRACES waits for `{{`, and never opens on an empty field.
   */
  suggestTrigger = TRIGGER_ALWAYS,
  ...props
}) => {
  const { t } = useTranslation();
  const [tooltip, setTooltip] = useState(null);
  // The hint is drawn at the cursor, so it needs clamping into the window the
  // same way the editor popup does — the offsets are the old ones, now the
  // STARTING point rather than the final one.
  const [tooltipRef, tooltipPlace] = usePointPlacement(
    tooltip ? { x: tooltip.x + 14, y: tooltip.y + 6 } : null
  );
  const [isFocused, setIsFocused] = useState(false);
  const [editingVariable, setEditingVariable] = useState(null);
  // "To Variable" menu: { x, y, start, end, text } — the plain text is SNAPSHOTTED at right-click
  // time because clicking the menu blurs the field (handleBlur repaints), and the replacement
  // has to be computed against what the user actually selected.
  const [selectionMenu, setSelectionMenu] = useState(null);
  // { items: string[], x: number, y: number, insertStart: number, active: number }
  const [dynSuggestions, setDynSuggestions] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);
  // setEditableRef must keep stable deps (see its comment), so it cannot read
  // `isExpanded` directly — it fires for BOTH the expand and the collapse swap
  // and has to tell them apart.
  const isExpandedRef = useRef(false);
  isExpandedRef.current = isExpanded;
  const [overlayRect, setOverlayRect] = useState(null);
  const editableRef = useRef(null);
  const anchorRef = useRef(null);
  /** The portaled overlay wrapper, so its OWN scrolling is not mistaken for the page's. */
  const overlayRef = useRef(null);
  const pendingCaretRef = useRef(null);
  // Last caret position we could resolve, so the expand transition is never
  // left without one (see resolveExpandCaret).
  const lastCaretRef = useRef(null);
  // Mirrors `editingVariable` synchronously — handleBlur and the click-outside listener below run
  // in the same tick as a click that opens/closes the popup, before React re-renders with the new
  // state, so they'd otherwise see a stale value through the closure.
  const editingVariableRef = useRef(null);
  // Same idea for the "To Variable" menu: handleBlur runs in the tick of the mousedown on the
  // menu item, before React re-renders — it must not collapse the overlay under the user.
  const selectionMenuRef = useRef(false);
  // URL field only: set synchronously by handleClick when the click landed on a variable span, so
  // the deferred (setTimeout) overflow-check in handleFocus can skip expanding for that click.
  const suppressExpandRef = useRef(false);
  const isUpdatingRef = useRef(false);
  const lastValueRef = useRef('');
  const lastRenderedTextRef = useRef('');
  const variableSourcesVersionRef = useRef('');

  const isKvField = String(className || '').includes('request-builder-kv-input');
  const showDynamicVarPicker = showDynamicVarPickerProp ?? isKvField;

  const {
    positionDynSuggestions, findVariableOptions, getVariableValue, createHighlightedHTML,
    getPlainText, getSelectionOffsets, stripDomNewlines, setSelectionOffsets, suggestionScopes,
  } = useHighlightDom({
    selectedEnvironment, workspaceVariables, globalVariables, collectionVariables, folderVariables,
    expandOnOverflow, setDynSuggestions, editableRef, isKvField,
  });

  const { scheduleDynamicVarPicker, openDynamicVarPicker } = useDynamicVarPicker({
    showDynamicVarPicker, suggest, suggestTrigger, setDynSuggestions, editableRef,
    positionDynSuggestions, getPlainText, getSelectionOffsets, suggestionScopes,
  });

  const {
    undoStackRef, redoStackRef, lastOpRef, commitHistory, renderHighlightedContent, setEditableRef,
    expandIfOverflowing, handleInput,
  } = useHighlightEditing({
    onChange, showDynamicVarPicker, inputRef, expandOnOverflow, isExpanded, setIsExpanded,
    isExpandedRef, setOverlayRect, editableRef, anchorRef, pendingCaretRef, lastCaretRef,
    suppressExpandRef, isUpdatingRef, lastValueRef, lastRenderedTextRef, createHighlightedHTML,
    getPlainText, getSelectionOffsets, setSelectionOffsets, openDynamicVarPicker,
  });

  useHighlightSync({
    value, selectedEnvironment, workspaceVariables, globalVariables, collectionVariables,
    folderVariables, forceSyncNonce, expandOnOverflow, setEditingVariable, isExpanded,
    setIsExpanded, setOverlayRect, editableRef, anchorRef, overlayRef, editingVariableRef,
    isUpdatingRef, lastValueRef, lastRenderedTextRef, variableSourcesVersionRef,
    renderHighlightedContent, getPlainText, getSelectionOffsets,
  });

  const {
    handleMouseMove, handleMouseLeave, handleContextMenu, closeSelectionMenu, openToVariableEditor,
    saveVariableValue, handleFocus, handleBlur, handleClick, refreshDynamicVarPicker, insertDynVar,
    handleKeyDown, handlePaste, handleMouseDown,
  } = useHighlightHandlers({
    value, onChange, selectedEnvironment, onVariableUpdate, variableContext, onPastePlainText,
    onBlurProp, disabled, showDynamicVarPicker, type, expandOnOverflow, selectionToVariable, props,
    setTooltip, setIsFocused, editingVariable, setEditingVariable, selectionMenu, setSelectionMenu,
    dynSuggestions, setDynSuggestions, isExpanded, setIsExpanded, editableRef, lastCaretRef,
    editingVariableRef, selectionMenuRef, suppressExpandRef, lastValueRef, getVariableValue,
    findVariableOptions, renderHighlightedContent, getPlainText, getSelectionOffsets,
    stripDomNewlines, setSelectionOffsets, openDynamicVarPicker, scheduleDynamicVarPicker,
    undoStackRef, redoStackRef, lastOpRef, commitHistory, expandIfOverflowing,
  });

  /**
   * Copy and cut write the PLAIN TEXT, never the DOM.
   *
   * Left to the browser, copying from this field serialises the selected
   * fragment — variable spans, attributes and all — and the text/plain it
   * derives from that carried newlines, so pasting a URL elsewhere produced
   * something broken across several lines. The field's value is a single
   * string by definition; that is what belongs on the clipboard.
   *
   * Cut re-uses `execCommand('delete')` rather than mutating state directly,
   * so the removal stays on the browser's own undo stack instead of being one
   * more edit the field cannot undo.
   */
  const writeSelectionAsPlainText = (e) => {
    const selected = window.getSelection?.()?.toString() ?? '';
    if (!selected) return false;
    e.preventDefault();
    // Newlines are stripped ONLY for the URL-shaped field, matching the policy
    // already applied to the value in handleInput: WKWebView inserts real \n
    // characters for what is meant to be soft wrap, and a URL has no
    // meaningful newlines. That strip fixes the VALUE but not the DOM, which
    // is why copying a wrapped URL produced one broken across lines — the
    // browser was serialising newlines the field had already disowned.
    //
    // Every other user of this component (bodies, descriptions, SQL) may
    // legitimately contain newlines, and stripping theirs would be a new bug.
    const text = expandOnOverflow ? selected.replace(/[\r\n]+/g, '') : selected;
    e.clipboardData?.setData('text/plain', text);
    return true;
  };

  const handleCopy = (e) => {
    writeSelectionAsPlainText(e);
  };

  const handleCut = (e) => {
    if (disabled) return;
    if (writeSelectionAsPlainText(e)) document.execCommand('delete');
  };

  /**
   * ARIA attributes the caller put on the field.
   *
   * `...props` is deliberately NOT spread wholesale — it carries handlers this
   * component routes itself (onKeyDown, onPaste, onContextMenu) and spreading
   * them would silently replace them. But dropping `aria-label` too left every
   * field an unnamed contenteditable, addressable neither by a screen reader
   * nor by `getByLabelText`, which is how a caller naturally labels one.
   */
  const ariaProps = Object.fromEntries(
    Object.entries(props).filter(([key]) => key.startsWith('aria-') || key === 'role')
  );

  const sharedEditableProps = {
    // First, so nothing the component sets itself can be overwritten by a caller.
    ...ariaProps,
    contentEditable: !disabled,
    suppressContentEditableWarning: true,
    dir: 'ltr',
    // URLs, header names and variable syntax are not prose. Browsers spellcheck
    // contentEditable by default, so every one of these fields was drawing red
    // squiggles under perfectly correct input.
    spellCheck: false,
    autoCorrect: 'off',
    autoCapitalize: 'off',
    onMouseDown: handleMouseDown,
    onInput: handleInput,
    onFocus: handleFocus,
    onBlur: handleBlur,
    onClick: handleClick,
    onKeyUp: refreshDynamicVarPicker,
    onKeyDown: handleKeyDown,
    onPaste: handlePaste,
    onCopy: handleCopy,
    onCut: handleCut,
    onContextMenu: handleContextMenu,
    onMouseMove: handleMouseMove,
    onMouseLeave: handleMouseLeave,
    'data-placeholder': !value ? placeholder : '',
  };

  return renderSyntaxHighlightInput({
    t, overlayRect, className, selectedEnvironment, workspaceVariables, globalVariables,
    collectionVariables, folderVariables, onVariableUpdate, variableContext, style,
    expandOnOverflow, tooltip, tooltipRef, tooltipPlace, isFocused, editingVariable,
    setEditingVariable, selectionMenu, dynSuggestions, setDynSuggestions, isExpanded,
    setEditableRef, anchorRef, overlayRef, editingVariableRef, isKvField, closeSelectionMenu,
    saveVariableValue, openToVariableEditor, insertDynVar, sharedEditableProps,
  });
};

export default SyntaxHighlightInput;
